/* WhyThisOutput — GDPR Art. 22 "right to explanation" popover.
 *
 * Tiny icon button that, when clicked, reveals:
 *   - Which AI model produced the output (GPT-5.2, Claude Sonnet 4.5, etc.)
 *   - What user data was used as input
 *   - Confidence / uncertainty qualifier
 *   - Link to methodology doc / Privacy Policy
 *
 * Drop this next to any AI-generated recommendation:
 *   <WhyThisOutput
 *     model="GPT-5.2"
 *     inputs={["lab values", "body metrics", "food log last 7d"]}
 *     method="Rule-based pharmacology + LLM narrative"
 *     docHref="/legal#ai-methodology"
 *   />
 */
import React, { useState } from "react";
import { HelpCircle, X, Sparkles, BookOpen } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";

export default function WhyThisOutput({ model, inputs, method, docHref, dataTestid }) {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full transition-opacity ${
          isDark ? "text-gray-400 hover:text-gray-200 bg-white/5 hover:bg-white/10"
                 : "text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200"
        }`}
        title={bg ? "Защо виждам това? (GDPR Чл. 22)" : "Why am I seeing this? (GDPR Art. 22)"}
        data-testid={dataTestid || "why-this-output-btn"}
      >
        <HelpCircle className="w-3 h-3" />
        {bg ? "Защо?" : "Why?"}
      </button>
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
          onClick={() => setOpen(false)}
          data-testid="why-this-output-modal"
        >
          <div
            className={`w-full max-w-md rounded-2xl p-5 ${isDark ? "bg-[hsl(223,47%,12%)] border border-white/10 text-gray-100" : "bg-white border border-gray-200 text-gray-900"}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-2 mb-3">
              <div>
                <h3 className="text-sm font-bold flex items-center gap-1.5">
                  <Sparkles className={`w-4 h-4 ${isDark ? "text-purple-400" : "text-purple-600"}`} />
                  {bg ? "Защо виждам това?" : "Why am I seeing this?"}
                </h3>
                <p className={`text-[10px] mt-0.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {bg ? "GDPR Чл. 22 · право на обяснение" : "GDPR Art. 22 · right to explanation"}
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className={`p-1 rounded-lg ${isDark ? "hover:bg-white/10 text-gray-400" : "hover:bg-gray-100 text-gray-500"}`}
                data-testid="why-this-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3 text-[12px]">
              {model && (
                <div>
                  <div className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${isDark ? "text-purple-300" : "text-purple-700"}`}>
                    {bg ? "AI модел" : "AI model"}
                  </div>
                  <div>{model}</div>
                </div>
              )}
              {inputs && inputs.length > 0 && (
                <div>
                  <div className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${isDark ? "text-purple-300" : "text-purple-700"}`}>
                    {bg ? "Какви данни ползвам" : "Input data"}
                  </div>
                  <ul className="list-disc list-inside space-y-0.5">
                    {inputs.map((i, idx) => <li key={idx}>{i}</li>)}
                  </ul>
                </div>
              )}
              {method && (
                <div>
                  <div className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${isDark ? "text-purple-300" : "text-purple-700"}`}>
                    {bg ? "Метод" : "Method"}
                  </div>
                  <div>{method}</div>
                </div>
              )}
              <div className={`rounded-lg p-2 border ${isDark ? "bg-amber-950/30 border-amber-800/40" : "bg-amber-50 border-amber-200"}`}>
                <p className={`text-[11px] italic ${isDark ? "text-amber-300" : "text-amber-800"}`}>
                  {bg
                    ? "⚕️ Това НЕ е медицинска диагноза. Резултатите са информативни и не заместват консултация с лекар. Имате право да поискате човешки преглед и оспорване."
                    : "⚕️ This is NOT a medical diagnosis. Results are informational and do not replace a doctor's consultation. You have the right to request human review and object."}
                </p>
              </div>
              {docHref && (
                <a
                  href={docHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center gap-1 text-[11px] font-semibold hover:underline ${isDark ? "text-sky-400" : "text-sky-600"}`}
                  data-testid="why-this-methodology-link"
                >
                  <BookOpen className="w-3 h-3" />
                  {bg ? "Подробна методология" : "Detailed methodology"}
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
